/**
 * Case folding for the seven tools whose entire job is an exact text transformation.
 *
 * `"İstanbul".toLowerCase()` is `"i̇stanbul"` — an `i` followed by U+0307, a combining
 * dot — where Turkish wants `"istanbul"`. The other direction is worse the other way:
 * `"ilgi".toUpperCase()` is `"ILGI"` where Turkish wants `"İLGİ"`. A case converter that
 * gets that wrong is not slightly off, it is giving the wrong answer.
 *
 * **Turkish is the only one of the site's seventeen locales where this is true.** A
 * 9,000-character sample folded through all of them is byte-identical to locale-less
 * folding in sixteen; only `tr` moves (and `az`, which the site does not publish). That
 * is why this module is Turkish-specific rather than a general locale fold: a 17-option
 * "text language" control would offer sixteen identical choices, and an interface that
 * pretends to a generality the data does not have is a worse interface.
 *
 * **This is for output, never for lookups.** `"IT".toLocaleLowerCase("tr")` is `"ıt"`,
 * which matches no key in `SKIP_WORDS`, `MINOR_WORDS` or any of the eleven other English
 * word tables in the tool corpus — a Turkish reader would silently lose every stop-word
 * match. Folding a lookup key, an ASCII alphabet (hex, morse, small caps) or a technical
 * identifier (an HTML tag name, a file extension) must stay locale-less. See
 * `docs/translation-decisions.md`.
 */

/** The locale whose casing rules differ. Not a list on purpose — see above. */
const TURKISH = "tr";

/** Lower-case for display: the user's own text, shown back to them. */
export function foldLower(text: string, turkish: boolean): string {
  return turkish ? text.toLocaleLowerCase(TURKISH) : text.toLowerCase();
}

/** Upper-case for display. */
export function foldUpper(text: string, turkish: boolean): string {
  return turkish ? text.toLocaleUpperCase(TURKISH) : text.toUpperCase();
}

/**
 * Does this character HAVE case at all — i.e. does it change when folded?
 *
 * The question every "is this a letter I can flip" test in the case tools is really
 * asking, and the reason they may not simply widen `[a-zA-Z]` to `\p{L}`. Case is a
 * property of a few scripts, not of letters in general: `toUpperCase()` is the identity
 * function in Arabic, Hebrew, Devanagari, Hangul, Han and kana, so a check that newly
 * admits them treats every such character as a lower-case letter waiting to be flipped.
 * `alternating case` would then walk its index across characters it cannot change, and
 * an "is this ALL CAPS" test would report every unicameral string as shouting.
 *
 * Locale-less on purpose, and not a `turkish` variant of the other three: whether `ş`
 * has case does not depend on who is reading. Only the *result* of folding it does.
 */
export function hasCase(char: string): boolean {
  return char.toLowerCase() !== char.toUpperCase();
}

/**
 * First character up, rest untouched — the shape every title-caser needs.
 *
 * Uses the code POINT, not `charAt(0)`: `charAt` splits a surrogate pair, and the
 * Turkish upper-casing of `i` is `İ`, which is one code point but not the one a naive
 * `[0]` would produce for an astral first character.
 */
export function capitalizeFirst(word: string, turkish: boolean): string {
  const first = [...word][0];
  if (first === undefined) {
    return word;
  }
  return foldUpper(first, turkish) + word.slice(first.length);
}
